import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import AlertModal from './AlertModal';
import ConfirmModal from './ConfirmModal';
import DatePicker from './DatePicker';
import { ServerIcon } from '../utils/serverIcons';
import './StatisticsMobile.css';
import './StatisticsManagers.css';
import './StatisticsManagersExtended.css';
import { SENIOR_AND_ABOVE, isManagerModeratorRole, isJuniorOnlyRoleName } from '../constants/rolePermissions';
import StatisticsManagersTable, { DayPillStrip } from './StatisticsManagersTable';
import { getMetricsForSection, MRP_METRICS, calcActivityScore, calcWeekDelta } from '../utils/mrpStatisticsMetrics';
import { exportMrpWeekToExcel, exportMrpWeekTableToExcel } from '../utils/exportMrpWeekExcel';
import {
  getCurrentWeekStartDate,
  weekStartToStr,
  readStatsSearchParams,
  buildStatsSearchParams,
} from '../utils/statisticsUrlParams';

const DESKTOP_EXPORT_MQ = '(min-width: 901px)';

const StatisticsManagers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlRef = useRef(null);
  if (!initialUrlRef.current) {
    initialUrlRef.current = readStatsSearchParams(
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : searchParams
    );
  }
  const initialUrl = initialUrlRef.current;
  /** Из ссылки (сводка) или клика; в URL при клике не пишем → F5 не открывает карточку. */
  const selectedModeratorIdRef = useRef(
    initialUrl.moderatorId ? String(initialUrl.moderatorId) : null
  );
  const weekStatsLoadIdRef = useRef(0);

  const [moderators, setModerators] = useState([]);
  const [selectedModerator, setSelectedModerator] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [weekStart, setWeekStart] = useState(
    () => initialUrl.weekStart || getCurrentWeekStartDate()
  );
  const [weekStats, setWeekStats] = useState(null);
  const [weekHistory, setWeekHistory] = useState(null);
  const [weekHistoryLoading, setWeekHistoryLoading] = useState(false);
  const activityRailRef = useRef(null);
  const [allModeratorsStats, setAllModeratorsStats] = useState([]);
  const [displayMode, setDisplayMode] = useState(
    () => (initialUrl.displayMode === 'table' ? 'table' : 'list')
  ); // 'list' or 'table'
  const [expandedMode, setExpandedMode] = useState(false); // режим раскрытия для скриншотов
  const [tableViewDay, setTableViewDay] = useState('week'); // 'week' | 1..7
  const [editingMetric, setEditingMetric] = useState(null);
  const [metricEditValue, setMetricEditValue] = useState('');
  const resetMetricEditState = useCallback(() => {
    setEditingMetric(null);
    setMetricEditValue('');
  }, []);
  const [savingMetric, setSavingMetric] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [isMobile, setIsMobile] = useState(false); // определение мобильного устройства
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false); // состояние выдвижной панели на мобильных
  const [vacations, setVacations] = useState([]); // отпуска для текущей недели
  const [sendingNotification, setSendingNotification] = useState(false);
  const [confirmNotificationOpen, setConfirmNotificationOpen] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState({ mrp: true, senior: true, other: true }); // фильтры категорий в списке
  const [servers, setServers] = useState([]);
  const [exportingWeek, setExportingWeek] = useState(false);
  const [exportingTable, setExportingTable] = useState(false);
  const exportTableRef = useRef(() => {});
  const [canExportWeek, setCanExportWeek] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_EXPORT_MQ).matches
  );

  // Модальное окно для ошибок
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });
  const [reprimandOpen, setReprimandOpen] = useState(false);
  const [reprimandReason, setReprimandReason] = useState('');
  const [reprimandIssuedAt, setReprimandIssuedAt] = useState('');
  const [reprimandSubmitting, setReprimandSubmitting] = useState(false);

  const formatDateForServer = useCallback((date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  const canIssueReprimand = useMemo(
    () => SENIOR_AND_ABOVE.some((role) => userRoles.includes(role)),
    [userRoles],
  );

  const openReprimandModal = useCallback(() => {
    const nick = weekStats?.moderator?.nickname || selectedModerator?.nickname;
    if (!nick || weekStats?.moderator?.deleted_at) return;
    setReprimandReason('');
    setReprimandIssuedAt(formatDateForServer(new Date()));
    setReprimandOpen(true);
  }, [weekStats?.moderator?.nickname, weekStats?.moderator?.deleted_at, selectedModerator?.nickname, formatDateForServer]);

  const closeReprimandModal = useCallback(() => {
    if (reprimandSubmitting) return;
    setReprimandOpen(false);
    setReprimandReason('');
  }, [reprimandSubmitting]);

  const handleSubmitReprimand = useCallback(async () => {
    const nick = weekStats?.moderator?.nickname || selectedModerator?.nickname;
    if (!nick || !reprimandReason.trim()) {
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Введите причину выговора',
        type: 'error',
      });
      return;
    }
    setReprimandSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/reprimands', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nickname: nick,
          reason: reprimandReason.trim(),
          issued_at: reprimandIssuedAt || formatDateForServer(new Date()),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Ошибка при выдаче выговора');
      }
      setReprimandOpen(false);
      setReprimandReason('');
      setAlertModal({
        isOpen: true,
        title: 'Готово',
        message: `Выговор выдан: ${nick}`,
        type: 'success',
      });
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: err.message || 'Не удалось выдать выговор',
        type: 'error',
      });
    } finally {
      setReprimandSubmitting(false);
    }
  }, [
    weekStats?.moderator?.nickname,
    selectedModerator?.nickname,
    reprimandReason,
    reprimandIssuedAt,
    formatDateForServer,
  ]);

  // Определяем мобильное устройство и автоматически переключаем режим
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      setIsMobile(mobile);
      // На мобильных всегда используем режим списка
      if (mobile && displayMode === 'table') {
        setDisplayMode('list');
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [displayMode]);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_EXPORT_MQ);
    const onChange = (e) => setCanExportWeek(e.matches);
    onChange(mq);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // В URL только неделя и режим — moderator не пишем, чтобы F5 не открывал карточку.
  useEffect(() => {
    const next = buildStatsSearchParams({
      weekStartStr: weekStartToStr(weekStart),
      moderatorId: null,
      displayMode: isMobile ? 'list' : displayMode,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [weekStart, displayMode, isMobile, searchParams, setSearchParams]);

  useEffect(() => {
    loadUserRoles();
    loadServers();
  }, []);

  useEffect(() => {
    loadVacations();
  }, [weekStart]);

  const loadUserRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/check-roles', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
    }
  };

  const loadServers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/servers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServers(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error loading servers:', e);
    }
  };

  const loadModerators = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const weekStartStr = weekStartToStr(weekStart);
      const response = await fetch(`/api/statistics/moderators?weekStartDate=${weekStartStr}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!response.ok) {
        console.error('Error loading moderators: HTTP', response.status);
        return;
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setModerators(list);

      const wantedId = selectedModeratorIdRef.current;
      if (wantedId) {
        const mod = list.find((m) => String(m.discord_id) === String(wantedId)) || null;
        setSelectedModerator(mod);
        if (!mod) {
          selectedModeratorIdRef.current = null;
          setWeekStats(null);
        }
      } else {
        setSelectedModerator((current) => {
          if (!current) return null;
          return list.find((m) => String(m.discord_id) === String(current.discord_id)) || null;
        });
      }
    } catch (error) {
      console.error('Error loading moderators:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeekStats = async () => {
    const discordId = selectedModerator?.discord_id || selectedModeratorIdRef.current;
    if (!discordId) return;
    const loadId = ++weekStatsLoadIdRef.current;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const weekStartStr = weekStartToStr(weekStart);
      const response = await fetch(
        `/api/statistics/week?moderatorDiscordId=${encodeURIComponent(String(discordId))}&weekStartDate=${weekStartStr}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        },
      );
      if (loadId !== weekStatsLoadIdRef.current) return;
      if (!response.ok) {
        console.error('Error loading week stats: HTTP', response.status);
        return;
      }
      const data = await response.json();
      if (loadId !== weekStatsLoadIdRef.current) return;
      setWeekStats(data);
      resetMetricEditState();
      loadWeekHistory(String(discordId), weekStartStr);
    } catch (error) {
      if (loadId === weekStatsLoadIdRef.current) {
        console.error('Error loading week stats:', error);
      }
    } finally {
      if (loadId === weekStatsLoadIdRef.current) setLoading(false);
    }
  };

  const loadWeekHistory = async (moderatorDiscordId, weekStartStr) => {
    if (!moderatorDiscordId || !weekStartStr) return;
    try {
      setWeekHistoryLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/statistics/week/history?moderatorDiscordId=${moderatorDiscordId}&weekStartDate=${weekStartStr}&weeks=4`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setWeekHistory(Array.isArray(data.weeks) ? data.weeks : []);
      } else {
        setWeekHistory([]);
      }
    } catch (error) {
      console.error('Error loading week history:', error);
      setWeekHistory([]);
    } finally {
      setWeekHistoryLoading(false);
    }
  };

  const loadAllModeratorsStats = async () => {
    try {
      setLoadingTable(true);
      const token = localStorage.getItem('token');
      const weekStartStr = weekStartToStr(weekStart);
      const response = await fetch(
        `/api/statistics/week/all?weekStartDate=${weekStartStr}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.ok) {
        const data = await response.json();
        setAllModeratorsStats(data);
        resetMetricEditState();
      }
    } catch (error) {
      console.error('Error loading all moderators stats:', error);
    } finally {
      setLoadingTable(false);
    }
  };

  const loadVacations = async () => {
    try {
      const token = localStorage.getItem('token');
      const start = new Date(weekStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      const startDateStr = weekStartToStr(start);
      const endDateStr = weekStartToStr(end);

      const response = await fetch(
        `/api/vacations?startDate=${startDateStr}&endDate=${endDateStr}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.ok) {
        const data = await response.json();
        setVacations(data);
      }
    } catch (error) {
      console.error('Error loading vacations:', error);
    }
  };

  // Проверка, есть ли отпуск у модератора на конкретную дату
  const isVacationDay = useCallback((moderatorDiscordId, dateStr) => {
    if (!dateStr || !moderatorDiscordId) return false;
    return vacations.some(
      v => v.moderator_discord_id === moderatorDiscordId && v.vacation_date === dateStr
    );
  }, [vacations]);

  const canEditPunishment = () => {
    return SENIOR_AND_ABOVE.some(role => userRoles.includes(role));
  };

  const getWeekStartStr = () => weekStartToStr(weekStart);

  // Классическая схема: список по неделе → стата по выбранному модератору
  useEffect(() => {
    loadModerators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  useEffect(() => {
    if (displayMode === 'table') {
      loadAllModeratorsStats();
      return;
    }
    if (selectedModerator?.discord_id) {
      loadWeekStats();
    } else {
      setWeekStats(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModerator?.discord_id, weekStart, displayMode]);

  const handleSaveMetric = async (moderatorId, dayOfWeek, field) => {
    if (savingMetric) return;

    try {
      setSavingMetric(true);
      const token = localStorage.getItem('token');
      let value = metricEditValue.trim();
      if (value === '' || value === '0') {
        value = 0;
      } else {
        const num = parseInt(value, 10);
        if (Number.isNaN(num) || num < 0 || num > 999) {
          handleCancelEditMetric();
          setSavingMetric(false);
          return;
        }
        value = num;
      }

      const response = await fetch('/api/statistics/mrp/metric', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moderatorDiscordId: moderatorId,
          weekStartDate: getWeekStartStr(),
          dayOfWeek,
          field,
          value,
        }),
      });

      if (response.ok) {
        handleCancelEditMetric();
        setTimeout(() => {
          if (displayMode === 'list' && selectedModerator?.discord_id === moderatorId) {
            loadWeekStats();
          } else if (displayMode === 'table') {
            loadAllModeratorsStats();
          }
        }, 100);
      } else {
        const error = await response.json();
        setAlertModal({
          isOpen: true,
          title: 'Ошибка',
          message: error.error || 'Ошибка при сохранении',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving metric:', error);
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Ошибка при сохранении',
        type: 'error',
      });
    } finally {
      setSavingMetric(false);
    }
  };

  const handleStartEditMetric = (moderatorId, day, field, currentValue) => {
    setEditingMetric({ moderatorId, day, field });
    setMetricEditValue(currentValue > 0 ? String(currentValue) : '');
  };

  const handleCancelEditMetric = () => {
    resetMetricEditState();
  };

  const renderMetricCellList = (moderatorId, dayStat, metric) => {
    const value = metric.key === 'minutes' ? (dayStat.minutes || 0) : (Number(dayStat[metric.key]) || 0);
    const isDuration = metric.format === 'duration';
    const isEditing =
      canEditPunishment() &&
      !isDuration &&
      editingMetric &&
      editingMetric.moderatorId === moderatorId &&
      editingMetric.day === dayStat.day &&
      editingMetric.field === metric.key;

    if (isDuration) {
      return <span>{formatDuration(value)}</span>;
    }

    if (isEditing) {
      return (
        <input
          type="number"
          min="0"
          max="999"
          value={metricEditValue}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 999 && val.length <= 3)) {
              setMetricEditValue(val);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              if (
                editingMetric &&
                editingMetric.moderatorId === moderatorId &&
                editingMetric.day === dayStat.day &&
                editingMetric.field === metric.key &&
                !savingMetric
              ) {
                handleSaveMetric(moderatorId, dayStat.day, metric.key);
              }
            }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') handleCancelEditMetric();
          }}
          className="statistics-mrp-ext-input"
          autoFocus
          placeholder="0"
        />
      );
    }

    return (
      <button
        type="button"
        className={`statistics-mrp-ext-value ${value > 0 ? 'has-value' : 'is-empty'} ${canEditPunishment() ? 'is-editable' : ''}`}
        onClick={() => canEditPunishment() && handleStartEditMetric(moderatorId, dayStat.day, metric.key, value)}
        disabled={!canEditPunishment()}
      >
        {value > 0 ? value : '—'}
      </button>
    );
  };

  const isSeniorRole = (role) => {
    const r = (role || '').toLowerCase();
    return r.includes('senior') || r.includes('chief') || r.includes('curator') || r.includes('deputy');
  };

  const handleSendNotification = async () => {
    if (sendingNotification) return;

    try {
      setSendingNotification(true);
      setConfirmNotificationOpen(false);
      const token = localStorage.getItem('token');

      const response = await fetch('/api/statistics/notification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        setAlertModal({
          isOpen: true,
          title: 'Успешно',
          message: data.message || 'Уведомление успешно отправлено',
          type: 'success'
        });
      } else {
        const error = await response.json();
        setAlertModal({
          isOpen: true,
          title: 'Ошибка',
          message: error.error || 'Ошибка при отправке уведомления',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Ошибка при отправке уведомления: ' + error.message,
        type: 'error'
      });
    } finally {
      setSendingNotification(false);
    }
  };

  // Мемоизируем обработчики навигации по неделям
  const goToPreviousWeek = useCallback(() => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(weekStart.getDate() - 7);
    setWeekStart(newWeekStart);
  }, [weekStart]);

  const goToNextWeek = useCallback(() => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(weekStart.getDate() + 7);
    setWeekStart(newWeekStart);
  }, [weekStart]);

  const goToCurrentWeek = useCallback(() => {
    setWeekStart(getCurrentWeekStartDate());
  }, []);

  const formatWeekRange = useCallback(() => {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const startStr = `${String(start.getDate()).padStart(2, '0')}.${String(start.getMonth() + 1).padStart(2, '0')}`;
    const endStr = `${String(end.getDate()).padStart(2, '0')}.${String(end.getMonth() + 1).padStart(2, '0')}`;
    return `${startStr} - ${endStr}`;
  }, [weekStart]);

  // Мемоизируем функции форматирования
  const formatDuration = useCallback((minutes) => {
    if (!minutes || minutes === 0) return '0м';
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    if (hours > 0) {
      return `${hours}ч ${mins}м`;
    }
    return `${mins}м`;
  }, []);

  const formatDayName = useCallback((day) => {
    const days = ['', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
    return days[day] || '';
  }, []);

  const formatDayNameShort = useCallback((day) => {
    const days = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    return days[day] || '';
  }, []);

  const formatDayDate = useCallback((dateStr) => {
    if (!dateStr) return '';
    try {
      // Парсим дату в формате YYYY-MM-DD, возвращаем DD.MM.YYYY
      const [year, month, day] = dateStr.split('-');
      if (year && month && day) {
        return `${String(parseInt(day)).padStart(2, '0')}.${String(parseInt(month)).padStart(2, '0')}.${year}`;
      }
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${d}.${m}.${y}`;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
    }
    return '';
  }, []);

  const serverIconMap = useMemo(() => {
    const m = {};
    servers.forEach((s) => {
      m[`RU${s.server_number}_${s.server_name}`] = s.icon_url || null;
    });
    return m;
  }, [servers]);

  const weekDayHeaders = useMemo(() => {
    const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const headers = [];
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    for (let day = 1; day <= 7; day++) {
      const d = new Date(start);
      d.setDate(start.getDate() + (day - 1));
      const dateStr = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
      headers[day - 1] = `${dayNames[day - 1]} - ${dateStr}`;
    }
    return headers;
  }, [weekStart]);

  const listViewMetrics = useMemo(() => {
    if (!weekStats?.moderator?.role) return getMetricsForSection(false);
    return getMetricsForSection(isSeniorRole(weekStats.moderator.role));
  }, [weekStats?.moderator?.role, weekStats?.totalMetrics]);

  const getListMetricTotal = useCallback((metric) => {
    if (!weekStats) return 0;
    if (metric.key === 'minutes') return weekStats.totalMinutes || 0;
    if (weekStats.totalMetrics?.[metric.key] != null) {
      return weekStats.totalMetrics[metric.key];
    }
    return (weekStats.dailyStats || []).reduce(
      (sum, day) => sum + (Number(day[metric.key]) || 0),
      0
    );
  }, [weekStats]);

  const weekCompare = useMemo(() => {
    const weeks = Array.isArray(weekHistory) ? weekHistory : [];
    const chronological = [...weeks].reverse().map((w) => ({
      ...w,
      score: calcActivityScore(w.totals),
    }));
    // Для текущей недели берём totals из weekStats (актуальнее после правок)
    if (chronological.length > 0 && weekStats?.totalMetrics) {
      chronological[chronological.length - 1] = {
        ...chronological[chronological.length - 1],
        totals: weekStats.totalMetrics,
        score: calcActivityScore(weekStats.totalMetrics),
      };
    } else if (chronological.length === 0 && weekStats?.totalMetrics) {
      chronological.push({
        weekStart: weekStats.weekStart,
        totals: weekStats.totalMetrics,
        score: calcActivityScore(weekStats.totalMetrics),
      });
    }

    const current = chronological[chronological.length - 1] || null;
    const previous = chronological.length > 1 ? chronological[chronological.length - 2] : null;
    const currentScore = current?.score ?? 0;
    const previousScore = previous?.score ?? 0;
    const delta = calcWeekDelta(currentScore, previousScore);
    const maxScore = Math.max(1, ...chronological.map((w) => w.score || 0));

    const cards = MRP_METRICS.map((metric) => {
      const series = chronological.map((w) => Number(w.totals?.[metric.key]) || 0);
      const value = series[series.length - 1] || 0;
      const prevValue = series.length > 1 ? series[series.length - 2] : 0;
      const cardDelta = calcWeekDelta(value, prevValue);
      const max = Math.max(1, ...series);
      const share = currentScore > 0 ? Math.round((value / currentScore) * 100) : 0;
      return {
        key: metric.key,
        label: metric.label,
        group: metric.group || 'mod',
        value,
        delta: cardDelta,
        series,
        max,
        share,
      };
    });

    return {
      ready: weeks.length > 0 || Boolean(weekStats?.totalMetrics),
      currentScore,
      delta,
      chronological,
      maxScore,
      cards,
    };
  }, [weekHistory, weekStats?.totalMetrics, weekStats?.weekStart]);

  const scrollActivityRail = useCallback((direction) => {
    const el = activityRailRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * (window.matchMedia('(max-width: 768px)').matches ? 156 : 208), behavior: 'smooth' });
  }, []);

  const handleExportWeek = useCallback(() => {
    if (!canExportWeek || exportingWeek || !weekStats || !listViewMetrics.length) return;
    setExportingWeek(true);
    try {
      const ok = exportMrpWeekToExcel({
        weekStats,
        metrics: listViewMetrics,
        formatDuration,
        formatDayName,
        formatDayDate,
        weekLabel: formatWeekRange(),
      });
      if (!ok) {
        setAlertModal({
          isOpen: true,
          title: 'Экспорт',
          message: 'Нет данных для выгрузки',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error exporting week stats:', error);
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Не удалось выгрузить статистику в Excel',
        type: 'error',
      });
    } finally {
      setExportingWeek(false);
    }
  }, [
    canExportWeek,
    exportingWeek,
    weekStats,
    listViewMetrics,
    formatDuration,
    formatDayName,
    formatDayDate,
    formatWeekRange,
  ]);

  const effectiveTableViewDay = expandedMode ? 'week' : tableViewDay;

  const tableMetricProps = useMemo(() => ({
    weekDayHeaders,
    canEdit: canEditPunishment(),
    editingCell: editingMetric,
    editValue: metricEditValue,
    savingMetric,
    onStartEdit: handleStartEditMetric,
    onEditValueChange: setMetricEditValue,
    onSaveEdit: handleSaveMetric,
    onCancelEdit: handleCancelEditMetric,
    isVacationDay,
    serverIconMap,
    formatDuration,
    viewDay: effectiveTableViewDay,
    onViewDayChange: setTableViewDay,
  }), [
    weekDayHeaders,
    userRoles,
    editingMetric,
    metricEditValue,
    savingMetric,
    vacations,
    servers,
    weekStart,
    effectiveTableViewDay,
  ]);

  // Мемоизируем функцию получения буквы для аватара
  const getAvatarLetter = useCallback((name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  }, []);

  // Клик только выбирает модератора — стату грузит отдельный эффект.
  const handleModeratorSelect = useCallback((moderator) => {
    if (!moderator?.discord_id) return;
    const discordId = String(moderator.discord_id);
    if (selectedModeratorIdRef.current === discordId && selectedModerator?.discord_id === discordId) {
      return;
    }
    selectedModeratorIdRef.current = discordId;
    setWeekStats(null);
    setSelectedModerator(moderator);
  }, [selectedModerator?.discord_id]);

  const formatServerLabel = useCallback((server) => {
    if (!server) return '';
    return String(server).replace('_', ' · ');
  }, []);

  const formatProfileDate = useCallback((value) => {
    if (!value) return null;
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  }, []);

  // Мемоизируем фильтрацию и категоризацию модераторов
  const { filteredModerators, mrpModerators, seniorModerators, otherModerators } = useMemo(() => {
    // Фильтруем модераторов по поисковому запросу
    const filtered = searchQuery 
      ? moderators.filter(mod => {
          const query = searchQuery.toLowerCase();
          const name = (mod.nickname || '').toLowerCase();
          const discordId = (mod.discord_id || '').toLowerCase();
          const server = (mod.server || '').toLowerCase();
          return name.includes(query) || discordId.includes(query) || server.includes(query);
        })
      : moderators;

    // Оптимизированная категоризация за один проход
    const senior = [];
    const mrp = [];
    const other = [];
    
    // Используем Set для быстрой проверки принадлежности
    const seniorSet = new Set();
    const mrpSet = new Set();
    
    // Первый проход: определяем Senior модераторов
    for (const mod of filtered) {
      const roleLower = (mod.role || '').toLowerCase();
      if (roleLower.includes('senior') || 
          roleLower.includes('chief') ||
          roleLower.includes('curator') ||
          roleLower.includes('deputy')) {
        senior.push(mod);
        seniorSet.add(mod.discord_id);
      }
    }
    
    // Второй проход: определяем MRP модераторов (исключая Senior)
    for (const mod of filtered) {
      if (seniorSet.has(mod.discord_id)) continue;
      
      const roleLower = (mod.role || '').toLowerCase();
      if (isManagerModeratorRole(mod.role)) {
        mrp.push(mod);
        mrpSet.add(mod.discord_id);
      }
    }
    
    // Третий проход: остальные модераторы
    for (const mod of filtered) {
      if (seniorSet.has(mod.discord_id) || mrpSet.has(mod.discord_id)) continue;
      
      const roleLower = (mod.role || '').toLowerCase().trim();
      if (isJuniorOnlyRoleName(roleLower)) {
        continue;
      }
      other.push(mod);
    }
    
    return {
      filteredModerators: filtered,
      mrpModerators: mrp,
      seniorModerators: senior,
      otherModerators: other
    };
  }, [moderators, searchQuery]);

  const renderManagerListItem = useCallback((moderator, { closeMobile = false } = {}) => (
    <button
      type="button"
      key={moderator.discord_id}
      className={[
        'statistics-mrp-moderator-item',
        selectedModerator?.discord_id === moderator.discord_id ? 'selected' : '',
        moderator.deleted_at ? 'deleted' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => {
        handleModeratorSelect(moderator);
        if (closeMobile) setMobileSidebarOpen(false);
      }}
    >
      <div className="statistics-mrp-moderator-avatar">
        <ServerIcon
          iconUrl={serverIconMap[moderator.server]}
          size={18}
          fallback={getAvatarLetter(moderator.nickname)}
        />
      </div>
      <div className="statistics-mrp-moderator-info">
        <div className="statistics-mrp-moderator-name">
          {moderator.nickname || '—'}
          {moderator.deleted_at ? <span className="statistics-mrp-deleted-badge">удалён</span> : null}
        </div>
        <div className="statistics-mrp-moderator-role">
          {formatServerLabel(moderator.server) || moderator.role || 'Manager'}
        </div>
      </div>
    </button>
  ), [selectedModerator, serverIconMap, handleModeratorSelect, getAvatarLetter, formatServerLabel]);

  const renderManagersSidebar = useCallback((options = {}) => {
    const { closeMobile = false, mobileOpen = false } = options;
    return (
      <aside className={`statistics-mrp-sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="statistics-mrp-sidebar-header">
          <div className="statistics-mrp-sidebar-heading">
            <h2>Менеджеры</h2>
            <span className="statistics-mrp-sidebar-count">{filteredModerators.length}</span>
          </div>
          <button type="button" className="statistics-mrp-sidebar-close" onClick={() => setMobileSidebarOpen(false)} aria-label="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div className="statistics-mrp-search">
          <input
            type="search"
            placeholder="Поиск по нику..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="statistics-mrp-category-filters" role="group" aria-label="Фильтр ролей">
          <button
            type="button"
            className={`statistics-mrp-filter-btn ${categoryFilters.mrp ? 'active' : ''}`}
            onClick={() => setCategoryFilters((prev) => ({ ...prev, mrp: !prev.mrp }))}
          >
            Manager
          </button>
          <button
            type="button"
            className={`statistics-mrp-filter-btn ${categoryFilters.senior ? 'active' : ''}`}
            onClick={() => setCategoryFilters((prev) => ({ ...prev, senior: !prev.senior }))}
          >
            Senior
          </button>
          <button
            type="button"
            className={`statistics-mrp-filter-btn ${categoryFilters.other ? 'active' : ''}`}
            onClick={() => setCategoryFilters((prev) => ({ ...prev, other: !prev.other }))}
          >
            Другое
          </button>
        </div>

        <div className="statistics-mrp-moderators-list">
          {categoryFilters.mrp && mrpModerators.length > 0 && (
            <div className="statistics-mrp-moderator-category">
              <div className="statistics-mrp-category-title">
                <span>Manager</span>
                <span className="statistics-mrp-category-count">{mrpModerators.length}</span>
              </div>
              {mrpModerators.map((m) => renderManagerListItem(m, { closeMobile }))}
            </div>
          )}
          {categoryFilters.senior && seniorModerators.length > 0 && (
            <div className="statistics-mrp-moderator-category">
              <div className="statistics-mrp-category-title">
                <span>Senior</span>
                <span className="statistics-mrp-category-count">{seniorModerators.length}</span>
              </div>
              {seniorModerators.map((m) => renderManagerListItem(m, { closeMobile }))}
            </div>
          )}
          {categoryFilters.other && otherModerators.length > 0 && (
            <div className="statistics-mrp-moderator-category">
              <div className="statistics-mrp-category-title">
                <span>Другое</span>
                <span className="statistics-mrp-category-count">{otherModerators.length}</span>
              </div>
              {otherModerators.map((m) => renderManagerListItem(m, { closeMobile }))}
            </div>
          )}
          {filteredModerators.length === 0 && (
            <div className="statistics-mrp-empty">Никого не найдено</div>
          )}
        </div>
      </aside>
    );
  }, [
    filteredModerators,
    mrpModerators,
    seniorModerators,
    otherModerators,
    searchQuery,
    categoryFilters,
    renderManagerListItem,
  ]);

  const isCurrentWeek = useMemo(() => {
    return weekStartToStr(weekStart) === weekStartToStr(getCurrentWeekStartDate());
  }, [weekStart]);

  const renderStatisticsToolbar = useCallback((options = {}) => {
    const { showMobileModerator = false } = options;

    return (
      <div className="statistics-mrp-toolbar">
        <div className="statistics-mrp-toolbar-start">
          {isMobile && showMobileModerator && (
            <div className="statistics-mrp-mobile-top-row">
              {selectedModerator ? (
                <button type="button" className="statistics-mrp-selector-moderator-btn" onClick={() => setMobileSidebarOpen(true)}>
                  <div className="statistics-mrp-mobile-select-avatar">{getAvatarLetter(selectedModerator.nickname)}</div>
                  <div className="statistics-mrp-mobile-select-info">
                    <div className="statistics-mrp-mobile-select-name">{selectedModerator.nickname}</div>
                    <div className="statistics-mrp-mobile-select-role">{selectedModerator.role}</div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
              ) : (
                <button type="button" className="statistics-mrp-selector-moderator-btn" onClick={() => setMobileSidebarOpen(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <span>Модератор</span>
                </button>
              )}
              <button
                type="button"
                className="statistics-mrp-toolbar-icon-btn statistics-mrp-notification-btn-mobile"
                onClick={() => setConfirmNotificationOpen(true)}
                disabled={sendingNotification}
                title="Уведомления"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </button>
            </div>
          )}

          <div className="statistics-mrp-week-switcher">
            <button type="button" className="statistics-mrp-week-switcher-btn" onClick={goToPreviousWeek} aria-label="Предыдущая неделя">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button
              type="button"
              className={`statistics-mrp-week-switcher-label ${isCurrentWeek ? 'is-current' : ''}`}
              onClick={!isCurrentWeek ? goToCurrentWeek : undefined}
              title={isCurrentWeek ? 'Текущая неделя' : 'Вернуться к текущей неделе'}
            >
              {formatWeekRange()}
            </button>
            <button type="button" className="statistics-mrp-week-switcher-btn" onClick={goToNextWeek} aria-label="Следующая неделя">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        </div>

        {displayMode === 'table' && !expandedMode && !isMobile && (
          <div className="statistics-mrp-toolbar-center">
            <DayPillStrip
              weekDayHeaders={weekDayHeaders}
              activeDay={tableViewDay}
              onSelect={setTableViewDay}
            />
          </div>
        )}

        <div className="statistics-mrp-toolbar-end">
          {!isMobile && (
            <button
              type="button"
              className="statistics-mrp-toolbar-icon-btn"
              onClick={() => setConfirmNotificationOpen(true)}
              disabled={sendingNotification}
              title="Уведомления"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </button>
          )}

          <div className="statistics-mrp-toolbar-segment">
            <button
              type="button"
              className={displayMode === 'list' ? 'active' : ''}
              onClick={() => !isMobile && setDisplayMode('list')}
              disabled={isMobile}
              title="Списком"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
            </button>
            <button
              type="button"
              className={displayMode === 'table' ? 'active' : ''}
              onClick={() => !isMobile && setDisplayMode('table')}
              disabled={isMobile}
              title="Таблицей"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
            </button>
          </div>

          {displayMode === 'table' && canExportWeek && (
            <button
              type="button"
              className="statistics-mrp-toolbar-icon-btn mrp-table-export-btn"
              onClick={() => exportTableRef.current()}
              disabled={exportingTable || loadingTable || loading}
              title="Скачать таблицу всех модераторов в Excel"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span className="mrp-table-export-label">{exportingTable ? '…' : 'Экспорт'}</span>
            </button>
          )}

          {displayMode === 'table' && (
            <button
              type="button"
              className={`statistics-mrp-toolbar-icon-btn statistics-mrp-toolbar-expand ${expandedMode ? 'active' : ''}`}
              onClick={() => setExpandedMode(!expandedMode)}
              title={expandedMode ? 'Свернуть' : 'Раскрыть для скриншота'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {expandedMode ? (
                  <>
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </>
                ) : (
                  <>
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="9" y2="15" />
                    <line x1="3" y1="21" x2="15" y2="9" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }, [
    isMobile,
    selectedModerator,
    getAvatarLetter,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    formatWeekRange,
    isCurrentWeek,
    sendingNotification,
    displayMode,
    expandedMode,
    weekDayHeaders,
    tableViewDay,
    canExportWeek,
    exportingTable,
    loadingTable,
    loading,
  ]);

  // Мемоизируем функцию сортировки по серверам
  const sortByServer = useCallback((moderators) => {
    // Кэшируем результаты парсинга серверов
    const serverCache = new Map();
    
    const getServerForSort = (server) => {
      if (serverCache.has(server)) {
        return serverCache.get(server);
      }
      
      let result;
      if (!server) {
        result = { prefix: 'zzz', num: 9999, isRU: false };
      } else {
        const ruMatch = server.match(/^RU(\d+)/i);
        if (ruMatch) {
          result = { prefix: 'RU', num: parseInt(ruMatch[1]), isRU: true };
        } else {
          result = { prefix: server.split('_')[0] || server, num: 0, isRU: false };
        }
      }
      
      serverCache.set(server, result);
      return result;
    };
    
    return [...moderators].sort((a, b) => {
      const aServer = getServerForSort(a.server);
      const bServer = getServerForSort(b.server);
      
      // RU серверы идут первыми, сортируются численно
      if (aServer.isRU && bServer.isRU) {
        return aServer.num - bServer.num;
      }
      
      // RU серверы перед не-RU
      if (aServer.isRU && !bServer.isRU) return -1;
      if (!aServer.isRU && bServer.isRU) return 1;
      
      // Не-RU серверы сортируются по алфавиту
      return aServer.prefix.localeCompare(bServer.prefix);
    });
  }, []);

  // Мемоизируем категоризацию модераторов для табличного отображения
  const tableModeratorsCategories = useMemo(() => {
    if (!allModeratorsStats || allModeratorsStats.length === 0) {
      return {
        mrpModerators: [],
        seniorModerators: [],
        otherModerators: []
      };
    }

    // Используем ту же логику, что и для списка
    const senior = [];
    const mrp = [];
    const other = [];
    
    // Используем Set для быстрой проверки принадлежности
    const seniorSet = new Set();
    const mrpSet = new Set();
    
    // Первый проход: определяем Senior модераторов
    for (const mod of allModeratorsStats) {
      const roleLower = (mod.role || '').toLowerCase();
      if (roleLower.includes('senior') || 
          roleLower.includes('chief') ||
          roleLower.includes('curator') ||
          roleLower.includes('deputy')) {
        senior.push(mod);
        seniorSet.add(mod.discord_id);
      }
    }
    
    // Второй проход: определяем MRP модераторов (исключая Senior)
    for (const mod of allModeratorsStats) {
      if (seniorSet.has(mod.discord_id)) continue;
      
      const roleLower = (mod.role || '').toLowerCase();
      if (roleLower.includes('junior') && !isManagerModeratorRole(mod.role)) {
        continue;
      }
      if (isManagerModeratorRole(mod.role)) {
        mrp.push(mod);
        mrpSet.add(mod.discord_id);
      }
    }
    
    // Третий проход: остальные модераторы
    for (const mod of allModeratorsStats) {
      if (seniorSet.has(mod.discord_id) || mrpSet.has(mod.discord_id)) continue;
      
      const roleLower = (mod.role || '').toLowerCase().trim();
      if (isJuniorOnlyRoleName(roleLower)) {
        continue;
      }
      other.push(mod);
    }
    
    return {
      mrpModerators: sortByServer(mrp),
      seniorModerators: sortByServer(senior),
      otherModerators: sortByServer(other)
    };
  }, [allModeratorsStats, sortByServer]);

  const handleExportTable = useCallback(() => {
    if (!canExportWeek || exportingTable || loadingTable) return;
    const sections = [
      {
        title: 'Manager',
        moderators: tableModeratorsCategories.mrpModerators,
        metrics: getMetricsForSection(false),
      },
      {
        title: 'Senior Manager',
        moderators: tableModeratorsCategories.seniorModerators,
        metrics: getMetricsForSection(true),
      },
    ];
    setExportingTable(true);
    try {
      const ok = exportMrpWeekTableToExcel({
        sections,
        formatDuration,
        weekLabel: formatWeekRange(),
      });
      if (!ok) {
        setAlertModal({
          isOpen: true,
          title: 'Экспорт',
          message: 'Нет данных для выгрузки таблицы',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error exporting table stats:', error);
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Не удалось выгрузить таблицу в Excel',
        type: 'error',
      });
    } finally {
      setExportingTable(false);
    }
  }, [
    canExportWeek,
    exportingTable,
    loadingTable,
    tableModeratorsCategories,
    formatDuration,
    formatWeekRange,
  ]);

  exportTableRef.current = handleExportTable;

  if (loading && !weekStats && moderators.length === 0) {
    return (
      <div className="statistics-mrp-loading">
        <div className="loader"></div>
        <p>Загрузка данных...</p>
      </div>
    );
  }

  return (
    <div className={`statistics-mrp-container ${displayMode === 'table' ? 'table-mode' : ''} ${expandedMode ? 'expanded-mode' : ''}`}>
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ isOpen: false, title: '', message: '', type: 'error' })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
      {reprimandOpen && (
        <div className="mrp-reprimand-overlay" onClick={closeReprimandModal}>
          <div className="mrp-reprimand-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="mrp-reprimand-title">
            <div className="mrp-reprimand-modal__head">
              <div className="mrp-reprimand-modal__title-wrap">
                <span className="mrp-reprimand-modal__icon" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                <div>
                  <h3 id="mrp-reprimand-title">Выдать выговор</h3>
                  <p className="mrp-reprimand-modal__subtitle">
                    {weekStats?.moderator?.nickname || selectedModerator?.nickname || 'Менеджер'}
                  </p>
                </div>
              </div>
              <button type="button" className="mrp-reprimand-modal__close" onClick={closeReprimandModal} title="Закрыть" disabled={reprimandSubmitting}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mrp-reprimand-modal__body">
              <label className="mrp-reprimand-field" htmlFor="stats-reprimand-reason">
                <span className="mrp-reprimand-field__label">Причина *</span>
                <textarea
                  id="stats-reprimand-reason"
                  value={reprimandReason}
                  onChange={(e) => setReprimandReason(e.target.value)}
                  placeholder="Кратко опишите причину выговора"
                  rows={4}
                  disabled={reprimandSubmitting}
                />
              </label>
              <label className="mrp-reprimand-field">
                <span className="mrp-reprimand-field__label">Дата выдачи</span>
                <div className="mrp-reprimand-field__date">
                  <DatePicker
                    selectedDate={(() => {
                      const dateStr = reprimandIssuedAt;
                      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                        const [y, m, d] = dateStr.split('-').map(Number);
                        return new Date(y, m - 1, d);
                      }
                      return new Date();
                    })()}
                    onDateChange={(date) => {
                      if (date instanceof Date && !Number.isNaN(date.getTime())) {
                        setReprimandIssuedAt(formatDateForServer(date));
                      }
                    }}
                  />
                </div>
                <span className="mrp-reprimand-field__hint">Снимется автоматически через 14 дней</span>
              </label>
            </div>
            <div className="mrp-reprimand-modal__footer">
              <button
                type="button"
                className="mrp-reprimand-modal__btn mrp-reprimand-modal__btn--ghost"
                onClick={closeReprimandModal}
                disabled={reprimandSubmitting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="mrp-reprimand-modal__btn mrp-reprimand-modal__btn--danger"
                onClick={handleSubmitReprimand}
                disabled={reprimandSubmitting}
              >
                {reprimandSubmitting ? 'Выдача…' : 'Выдать выговор'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmNotificationOpen}
        onClose={() => setConfirmNotificationOpen(false)}
        onConfirm={handleSendNotification}
        title="Подтвердите отправку уведомления"
        message="Вы уверены что хотите отправить уведомление об обновлённой статистике менеджерам?"
        confirmLabel="Отправить"
        cancelLabel="Отмена"
      />
      {/* На мобильном при открытой панели — рендер в body через Portal */}
      {isMobile && mobileSidebarOpen && displayMode === 'list' && typeof document !== 'undefined' && createPortal(
        <>
          <div className="statistics-mrp-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
          {renderManagersSidebar({ closeMobile: true, mobileOpen: true })}
        </>,
        document.body
      )}

      {/* Боковая панель в потоке документа (десктоп или мобильный закрыт) */}
      {displayMode === 'list' && !(isMobile && mobileSidebarOpen) && (
        renderManagersSidebar({ closeMobile: isMobile })
      )}

      {/* Основная область */}
      <main className="statistics-mrp-main">
        {displayMode === 'list' && !selectedModerator ? (
          <>
            {renderStatisticsToolbar({ showMobileModerator: true })}
            <div className="statistics-mrp-empty-state">
              <svg 
                className="statistics-mrp-empty-state-icon"
                width="80" 
                height="80" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <p>Выберите модератора для просмотра статистики</p>
            </div>
          </>
        ) : (
          <>
            {renderStatisticsToolbar({ showMobileModerator: true })}

            {/* Информация о модераторе и статистике - режим списком */}
            {displayMode === 'list' && !weekStats && (
              <div className="statistics-mrp-loading" style={{ minHeight: 240 }}>
                <div className="loader"></div>
                <p>Загрузка статистики...</p>
              </div>
            )}

            {displayMode === 'list' && weekStats && (
              <div key={selectedModerator?.discord_id || 'empty'} className="statistics-mrp-content mrp-list">
                <div className="mrp-list__head">
                  <div className="mrp-list__identity">
                    <ServerIcon
                      iconUrl={serverIconMap[weekStats.moderator.server || selectedModerator?.server]}
                      size={isMobile ? 28 : 34}
                      className="mrp-list__icon"
                      fallback={
                        <span className="mrp-list__icon-fallback">
                          {getAvatarLetter(weekStats.moderator.nickname || selectedModerator?.nickname)}
                        </span>
                      }
                    />
                  </div>
                  <div className="mrp-list__meta">
                    <div className="mrp-list__meta-cell">
                      <span className="mrp-list__meta-label">Ник</span>
                      <div className="mrp-list__meta-top">
                        <span className="mrp-list__meta-value mrp-list__meta-value--name">
                          {weekStats.moderator.nickname || '—'}
                        </span>
                        {weekStats.moderator.deleted_at ? (
                          <span className="mrp-list__badge mrp-list__badge--deleted">удалён</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="mrp-list__meta-sep" aria-hidden />
                    <div className="mrp-list__meta-cell">
                      <span className="mrp-list__meta-label">Angry Space</span>
                      <span className="mrp-list__meta-value mrp-list__meta-value--as">
                        {weekStats.moderator.angry_space || selectedModerator?.angry_space || '—'}
                      </span>
                    </div>
                    <div className="mrp-list__meta-sep" aria-hidden />
                    <div className="mrp-list__meta-cell">
                      <span className="mrp-list__meta-label">Роль</span>
                      <span className="mrp-list__meta-value mrp-list__meta-value--role">
                        {weekStats.moderator.role || '—'}
                      </span>
                    </div>
                    <div className="mrp-list__meta-sep" aria-hidden />
                    <div className="mrp-list__meta-cell">
                      <span className="mrp-list__meta-label">В команде</span>
                      <span className="mrp-list__meta-value">
                        {weekStats.moderator.created_at
                          ? `с ${formatProfileDate(weekStats.moderator.created_at)}`
                          : '—'}
                      </span>
                      {weekStats.moderator.restored_at && !weekStats.moderator.deleted_at ? (
                        <span className="mrp-list__meta-note mrp-list__meta-note--ok">
                          восст. {formatProfileDate(weekStats.moderator.restored_at)}
                        </span>
                      ) : null}
                      {weekStats.moderator.deleted_at ? (
                        <span className="mrp-list__meta-note mrp-list__meta-note--gone">
                          удалён {formatProfileDate(weekStats.moderator.deleted_at)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="mrp-list__actions">
                    {canIssueReprimand && !weekStats.moderator.deleted_at && (
                      <button
                        type="button"
                        className="mrp-list__reprimand"
                        onClick={openReprimandModal}
                        title="Выдать выговор"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span>Выговор</span>
                      </button>
                    )}
                    {canExportWeek && (
                      <button
                        type="button"
                        className="mrp-list__export"
                        onClick={handleExportWeek}
                        disabled={exportingWeek || loading}
                        title="Скачать карточку в Excel"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <span>{exportingWeek ? '…' : 'Экспорт карточки'}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="mrp-list__insight" aria-label="Активность за неделю">
                  {weekHistoryLoading && !weekCompare.ready ? (
                    <div className="mrp-list__insight-loading">Считаем активность…</div>
                  ) : weekCompare.ready ? (
                    <div className="mrp-list__activity">
                      <div className="mrp-list__activity-main">
                        <div className="mrp-list__activity-label">Общая активность</div>
                        <div className="mrp-list__activity-row">
                          <div className="mrp-list__activity-value">{weekCompare.currentScore}</div>
                          <div className={`mrp-list__delta mrp-list__delta--${weekCompare.delta.direction}`}>
                            <span className="mrp-list__delta-icon" aria-hidden>
                              {weekCompare.delta.direction === 'up' ? '▲' : weekCompare.delta.direction === 'down' ? '▼' : '●'}
                            </span>
                            <span>
                              {weekCompare.delta.diff > 0 ? '+' : ''}
                              {weekCompare.delta.diff}
                            </span>
                            <span className="mrp-list__delta-pct">
                              {weekCompare.delta.percent > 0 ? '+' : ''}{weekCompare.delta.percent}%
                            </span>
                          </div>
                        </div>
                        <div className="mrp-list__spark" title="Последние 4 недели" aria-hidden>
                          {weekCompare.chronological.map((w, idx) => {
                            const ratio = (w.score || 0) / weekCompare.maxScore;
                            const px = Math.max(3, Math.round(ratio * 16));
                            const isCurrent = idx === weekCompare.chronological.length - 1;
                            return (
                              <span
                                key={w.weekStart || idx}
                                className={`mrp-list__spark-bar ${isCurrent ? 'is-current' : ''} ${(w.score || 0) > 0 ? 'has-value' : ''}`}
                                style={{ height: `${px}px` }}
                              />
                            );
                          })}
                        </div>
                        <div className="mrp-list__activity-hint">* По кол-ву действий</div>
                      </div>

                      <div className="mrp-list__activity-rail-wrap">
                        <button
                          type="button"
                          className="mrp-list__rail-btn"
                          onClick={() => scrollActivityRail(-1)}
                          aria-label="Листать метрики влево"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                        </button>
                        <div
                          className="mrp-list__activity-rail"
                          ref={activityRailRef}
                          tabIndex={0}
                          aria-label="Метрики активности, листайте в стороны"
                        >
                          {weekCompare.cards.map((card) => (
                            <div
                              key={card.key}
                              className={`mrp-list__activity-card mrp-list__activity-card--${card.group} ${card.value > 0 ? 'has-value' : ''}`}
                              title={card.label}
                            >
                              <div className="mrp-list__activity-card-top">
                                <span className="mrp-list__activity-card-label">{card.label}</span>
                                <span className={`mrp-list__card-delta mrp-list__card-delta--${card.delta.direction}`}>
                                  {card.delta.diff === 0
                                    ? '0'
                                    : `${card.delta.direction === 'up' ? '▲' : '▼'}${card.delta.diff > 0 ? '+' : ''}${card.delta.diff}`}
                                </span>
                              </div>
                              <div className="mrp-list__activity-card-mid">
                                <span className="mrp-list__activity-card-value">
                                  {card.value > 0 ? card.value : '—'}
                                </span>
                                {card.share > 0 && (
                                  <span className="mrp-list__activity-card-share">{card.share}%</span>
                                )}
                              </div>
                              <div className="mrp-list__activity-card-bars" aria-hidden>
                                {card.series.map((v, idx) => {
                                  const ratio = v / card.max;
                                  const px = Math.max(2, Math.round(ratio * 10));
                                  const isCurrent = idx === card.series.length - 1;
                                  return (
                                    <span
                                      key={`${card.key}-${idx}`}
                                      className={`mrp-list__activity-card-bar ${isCurrent ? 'is-current' : ''} ${v > 0 ? 'has-value' : ''}`}
                                      style={{ height: `${px}px` }}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="mrp-list__rail-btn"
                          onClick={() => scrollActivityRail(1)}
                          aria-label="Листать метрики вправо"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mrp-list__insight-empty">Нет данных по активности</div>
                  )}
                </div>

                <div className="mrp-list__table-wrap">
                  <table className="mrp-list__table">
                    <thead>
                      <tr>
                        <th>День</th>
                        <th>Дата</th>
                        {listViewMetrics.map((metric) => (
                          <th key={metric.key}>{metric.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {weekStats.dailyStats.map((dayStat, index) => {
                        const isVacation = isVacationDay(selectedModerator?.discord_id, dayStat.date);
                        return (
                          <tr
                            key={index}
                            className={isVacation ? 'is-vacation' : ''}
                          >
                            <td className="mrp-list__day">{formatDayNameShort(dayStat.day)}</td>
                            <td className="mrp-list__date">{formatDayDate(dayStat.date)}</td>
                            {listViewMetrics.map((metric) => (
                              <td key={metric.key}>
                                {renderMetricCellList(selectedModerator.discord_id, dayStat, metric)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="mrp-list__total-spacer" aria-hidden="true" />
                        <td className="mrp-list__total-label">Итого</td>
                        {listViewMetrics.map((metric) => {
                          const total = getListMetricTotal(metric);
                          return (
                            <td key={metric.key} className="mrp-list__total-val">
                              <span className={`mrp-list__sum ${total > 0 ? 'has-value' : ''} ${metric.format === 'duration' ? 'is-time' : ''}`}>
                                {metric.format === 'duration'
                                  ? formatDuration(total)
                                  : (total > 0 ? total : '—')}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="mrp-list__mobile">
                  {weekStats.dailyStats.map((dayStat, index) => {
                    const isVacation = isVacationDay(selectedModerator?.discord_id, dayStat.date);
                    return (
                      <div
                        key={index}
                        className={`mrp-list__card ${isVacation ? 'is-vacation' : ''}`}
                      >
                        <div className="mrp-list__card-head">
                          {formatDayName(dayStat.day)}
                          <span>{formatDayDate(dayStat.date)}</span>
                        </div>
                        <div className="mrp-list__card-rows">
                          {listViewMetrics.map((metric) => (
                            <div key={metric.key} className="mrp-list__card-row">
                              <span>{metric.label}</span>
                              {renderMetricCellList(selectedModerator.discord_id, dayStat, metric)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <div className="mrp-list__card mrp-list__card--totals">
                    <div className="mrp-list__card-head">Итого за неделю</div>
                    <div className="mrp-list__card-rows">
                      {listViewMetrics.map((metric) => {
                        const total = getListMetricTotal(metric);
                        return (
                          <div key={metric.key} className="mrp-list__card-row">
                            <span>{metric.label}</span>
                            <span className={`mrp-list__sum ${total > 0 ? 'has-value' : ''} ${metric.format === 'duration' ? 'is-time' : ''}`}>
                              {metric.format === 'duration'
                                ? formatDuration(total)
                                : (total > 0 ? total : '—')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Табличное отображение всех модераторов */}
            {displayMode === 'table' && (
              <div key="table-view" className="statistics-mrp-table-view">
                {loadingTable ? (
                  <div className="statistics-mrp-loading">
                    <div className="loader"></div>
                    <p>Загрузка данных...</p>
                  </div>
                ) : (
                  <>
                    {tableModeratorsCategories.mrpModerators.length > 0 && (
                      <StatisticsManagersTable
                        title="Manager"
                        moderators={tableModeratorsCategories.mrpModerators}
                        metrics={getMetricsForSection(false)}
                        {...tableMetricProps}
                      />
                    )}
                    {tableModeratorsCategories.seniorModerators.length > 0 && (
                      <StatisticsManagersTable
                        title="Senior Manager"
                        moderators={tableModeratorsCategories.seniorModerators}
                        metrics={getMetricsForSection(true)}
                        {...tableMetricProps}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default StatisticsManagers;

